import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
} from '@mui/material';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { candidateAPI } from '../../utils/api';
import useScreenShare from '../../hooks/useScreenShare';
import useRecording from '../../hooks/useRecording';
import { saveRecordingBackup, markRecordingAsUploaded, cleanupOldBackups } from '../../utils/recordingBackup';
import { getScreenShareCaptureInfo } from '../../utils/screenShareCapture';
import { scheduleTimeoutBeeps, playFinalBeep } from '../../utils/timeoutBeeps';
import { scheduleTimeoutNotifications, showFinalNotification } from '../../utils/timeoutNotifications';
import RecordingTimerBar from './RecordingTimerBar';
import screenShareImage from '../../utils/img/Screen share tooltip.jpg';

const DEFAULT_TIME_PER_QUESTION_MINUTES = 15;

const QuestionView = ({ assignment, question, timePerQuestionMinutes, onComplete, onBack }) => {
  const { t } = useTranslation();
  const [questionData, setQuestionData] = useState(null);
  const [comments, setComments] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const commentsRef = useRef('');
  const beepsRef = useRef(null);
  const notificationsRef = useRef(null);
  const submittedRef = useRef(false);
  const screenShareCaptureInfoRef = useRef({
    display_surface: null,
    system_audio_captured: null,
  });

  const totalMs = Math.max(
    60 * 1000,
    (timePerQuestionMinutes || DEFAULT_TIME_PER_QUESTION_MINUTES) * 60 * 1000
  );

  const {
    screenShareActive,
    screenStream,
    error: screenShareError,
    detectScreenShare,
  } = useScreenShare();

  const {
    isRecording,
    elapsedTime,
    error: recordingError,
    startRecording,
    stopRecording,
  } = useRecording();

  // Keep the latest comments value accessible inside the auto-stop callback,
  // which captures state via closure.
  useEffect(() => {
    commentsRef.current = comments;
  }, [comments]);

  // Add beforeunload handler to prevent accidental navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isRecording) {
        e.preventDefault();
        e.returnValue = 'You are currently recording. If you leave this page, your recording will be lost and you will not be able to attempt this question again.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isRecording]);

  // Cancel any pending warning/urgent beeps and notifications when the
  // component unmounts.
  useEffect(() => {
    return () => {
      if (beepsRef.current) {
        beepsRef.current.cancel();
        beepsRef.current = null;
      }
      if (notificationsRef.current) {
        notificationsRef.current.cancel();
        notificationsRef.current = null;
      }
    };
  }, []);

  const handleStartQuestion = async () => {
    setError('');
    setLoading(true);
    screenShareCaptureInfoRef.current = {
      display_surface: null,
      system_audio_captured: null,
    };

    try {
      // Request screen share
      const stream = await detectScreenShare();
      screenShareCaptureInfoRef.current = getScreenShareCaptureInfo(stream);

      // Fetch question content
      const data = await candidateAPI.startQuestion(question.question_id, assignment.id, true);
      setQuestionData(data);

      // Start recording with a hard time limit; on timeout, auto-submit.
      await startRecording(stream, {
        maxDuration: totalMs,
        onAutoStop: () => handleSubmit(true),
      });

      // Schedule the 70% / 90% audio cues and matching OS notifications.
      beepsRef.current = scheduleTimeoutBeeps(totalMs);
      notificationsRef.current = scheduleTimeoutNotifications(totalMs, {
        warningTitle: t('candidate.notifications.warningTitle'),
        warningBody: t('candidate.notifications.warningBody'),
        urgentTitle: t('candidate.notifications.urgentTitle'),
        urgentBody: t('candidate.notifications.urgentBody'),
      });
    } catch (err) {
      setError(err.message || t('candidate.questionView.startError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (isAutoStop = false) => {
    // Manual clicks require an active recording. Auto-stop bypasses this guard
    // because useRecording clears isRecording once the auto-stop timer fires.
    if (!isAutoStop && !isRecording) {
      alert(t('candidate.questionView.noRecording'));
      return;
    }

    // Guard against double-submit: if auto-stop fires while the user is already
    // uploading, ignore it; if the user clicks after an auto-stop started, ignore.
    if (submittedRef.current) {
      return;
    }
    submittedRef.current = true;

    // Play the final cue BEFORE cancelling scheduled beeps, and only when time
    // actually ran out (not on manual submit). The notification mirrors the beep.
    if (isAutoStop) {
      playFinalBeep();
      showFinalNotification(
        t('candidate.notifications.finalTitle'),
        t('candidate.notifications.finalBody')
      );
    }

    // Cancel pending beeps and notifications so nothing fires after we've finished.
    if (beepsRef.current) {
      beepsRef.current.cancel();
      beepsRef.current = null;
    }
    if (notificationsRef.current) {
      notificationsRef.current.cancel();
      notificationsRef.current = null;
    }

    setUploading(true);

    try {
      // Stop recording and get blobs
      const { screenBlob, webcamBlob, audioBlob } = await stopRecording();

      // Validate that we have at least screen recording data
      if (!screenBlob || screenBlob.size === 0) {
        throw new Error('Screen recording failed - no data captured. Please try again.');
      }

      // Validate minimum file sizes (screen recording should be at least 1KB for any meaningful content)
      const minSize = 1024; // 1KB
      if (screenBlob.size < minSize) {
        throw new Error('Screen recording appears corrupted (file too small). Please try again.');
      }

      // Log recording sizes for debugging
      console.log('Recording sizes:', {
        screen: screenBlob ? `${(screenBlob.size / 1024 / 1024).toFixed(2)} MB` : 'N/A',
        webcam: webcamBlob ? `${(webcamBlob.size / 1024 / 1024).toFixed(2)} MB` : 'N/A',
        audio: audioBlob ? `${(audioBlob.size / 1024 / 1024).toFixed(2)} MB` : 'N/A'
      });

      // Validate file sizes (1GB limit)
      const maxSize = 1024 * 1024 * 1024; // 1GB in bytes

      if (screenBlob && screenBlob.size > maxSize) {
        throw new Error(t('candidate.questionView.fileSizeError', { type: 'Screen' }));
      }
      if (webcamBlob && webcamBlob.size > maxSize) {
        throw new Error(t('candidate.questionView.fileSizeError', { type: 'Webcam' }));
      }
      if (audioBlob && audioBlob.size > maxSize) {
        throw new Error(t('candidate.questionView.fileSizeError', { type: 'Audio' }));
      }

      const submittedComments = commentsRef.current;

      // Create form data
      const formData = new FormData();
      formData.append('assignment_id', assignment.id);
      formData.append('question_id', question.question_id);
      formData.append('comments', submittedComments);
      const captureInfo = screenShareCaptureInfoRef.current;
      if (captureInfo.system_audio_captured !== null) {
        formData.append('system_audio_captured', String(captureInfo.system_audio_captured));
      }
      if (captureInfo.display_surface) {
        formData.append('display_surface', captureInfo.display_surface);
      }

      if (screenBlob) {
        formData.append('screen_recording', screenBlob, 'screen.webm');
      }
      if (webcamBlob) {
        formData.append('webcam_recording', webcamBlob, 'webcam.webm');
      }
      if (audioBlob) {
        formData.append('audio_recording', audioBlob, 'audio.webm');
      }

      // Backup to IndexedDB before upload
      let backupId;
      try {
        backupId = await saveRecordingBackup({
          assignment_id: assignment.id,
          question_id: question.question_id,
          screenBlob,
          webcamBlob,
          audioBlob,
          comments: submittedComments,
        });
        console.log('Recording backed up to IndexedDB before upload');
      } catch (backupError) {
        console.warn('Failed to backup recording to IndexedDB:', backupError);
        // Continue with upload even if backup fails
      }

      // Upload recording with progress tracking
      await candidateAPI.uploadRecording(formData, (progress) => {
        setUploadProgress(progress);
      });

      // Mark backup as uploaded
      if (backupId) {
        try {
          await markRecordingAsUploaded(backupId);
          // Cleanup old backups in background
          cleanupOldBackups().catch(err => console.warn('Cleanup failed:', err));
        } catch (markError) {
          console.warn('Failed to mark backup as uploaded:', markError);
        }
      }

      if (isAutoStop) {
        alert(t('candidate.questionView.autoSubmitted'));
      } else {
        alert(t('candidate.questionView.submitSuccess'));
      }
      onComplete();
    } catch (err) {
      submittedRef.current = false;
      setError(t('candidate.questionView.submitError') + ' ' + err.message);
      setUploading(false);
    }
  };

  if (!questionData) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            {t('candidate.questionView.title', { number: question.order_number })}
          </Typography>
          <Button variant="outlined" onClick={onBack}>
            {t('candidate.questionView.backToQuestions')}
          </Button>
        </Box>

        <Paper sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {screenShareError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {screenShareError}
            </Alert>
          )}

          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>{t('candidate.questionView.important')}</strong> {t('candidate.questionView.screenShareRequired')}
          </Alert>

          <Alert severity="info" sx={{ mb: 3 }}>
            {t('candidate.questionView.timeLimitNotice', { minutes: timePerQuestionMinutes || DEFAULT_TIME_PER_QUESTION_MINUTES })}
          </Alert>

          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            {t('candidate.questionView.beforeYouBegin')}
          </Typography>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('candidate.questionView.shareEntireScreen')}
                    secondary={t('candidate.questionView.shareEntireScreenDesc')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('candidate.questionView.enableWebcam')}
                    secondary={t('candidate.questionView.enableWebcamDesc')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('candidate.questionView.enableMicrophone')}
                    secondary={t('candidate.questionView.enableMicrophoneDesc')}
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <img
                  src={screenShareImage}
                  alt="Screen sharing instructions"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<ScreenShareIcon />}
              onClick={handleStartQuestion}
              disabled={loading}
            >
              {loading ? t('candidate.questionView.starting') : t('candidate.questionView.startButton')}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5">
            {t('candidate.questionView.title', { number: questionData.order_number })}
          </Typography>
          {isRecording && (
            <Chip
              icon={<FiberManualRecordIcon />}
              label={t('candidate.questionView.recording')}
              color="error"
              sx={{ animation: 'pulse 2s infinite' }}
            />
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {recordingError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {recordingError}
        </Alert>
      )}

      {isRecording && (
        <RecordingTimerBar
          elapsedMs={elapsedTime}
          totalMs={totalMs}
          unit="m:ss"
          showChip={false}
          warningMessage={
            <>
              <strong>{t('candidate.questionView.warning')}</strong> {t('candidate.questionView.autoSubmitWarning')}
            </>
          }
        />
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('candidate.questionView.questionLabel')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, fontSize: '1.2rem' }}>
          {questionData.question_text}
        </Typography>
      </Paper>


      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="error"
          size="large"
          startIcon={<StopCircleIcon />}
          onClick={() => handleSubmit(false)}
          disabled={!isRecording || uploading}
          fullWidth
        >
          {uploading ? t('candidate.questionView.submitting') : t('candidate.questionView.stopAndSubmit')}
        </Button>
      </Box>

      {uploading && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Uploading recording... {uploadProgress}%
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%', mr: 1 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography variant="body2" color="text.secondary">{uploadProgress}%</Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>{t('candidate.questionView.note')}</strong> {t('candidate.questionView.noteMessage')}
        </Alert>

        <Typography variant="subtitle1" gutterBottom>
          {t('candidate.questionView.commentsLabel')}
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder={t('candidate.questionView.commentsPlaceholder')}
          variant="outlined"
        />
      </Paper>

    </Box>
  );
};

export default QuestionView;
